/**
 * PhotoMarkerManager - Manages photo marker numbering and configuration.
 *
 * Tracks:
 * - Current marker number (auto-increment)
 * - Active shape configuration
 * - Creation mode (entity or block)
 * - Initializes counter from XRecord on file open
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import { trackShape, trackShapeChange } from "@/analytics/drawing-analytics";
import { format as formatDate } from "date-fns";
import { deleteObj } from "../core/delete";
import { isPhotoMarkerEntity } from "../photomarker/detection";
import { createPhotoMarker } from "../photomarker";
import {
  savePhotomarkerLastNumber,
  getPhotomarkerLastNumber,
} from "../photomarker/xrecord";
import { AddPhotoMarkerCommand } from "../commands/AddPhotoMarkerCommand";
import { SetPhotoMarkerScaleCommand } from "../commands/SetPhotoMarkerScaleCommand";
import { getEntityHandle } from "../core/oda";
import { photoMarkers } from "@/blocks/photomarker";
import type { Textstyle, EntityProps } from "@/types";
import { selectionService } from "../selection";
import { applyEntityProps } from "../entities/properties";
import { isBlockReference } from "../entities/detection";
import {
  getPhotoMarkerTextHeight,
  rescalePhotoMarker,
  getPhotoMarkerAttributeValue,
  setPhotoMarkerAttributeValue,
} from "../photomarker/transform";
import { SetPhotoMarkerAttributeCommand } from "../commands/SetPhotoMarkerAttributeCommand";

/**
 * Photo marker shape types.
 */
export type PhotoMarkerShape =
  | "circle"
  | "large_arrow"
  | "thin_arrow"
  | "text_only";

/**
 * Photo marker creation mode.
 * - 'entity': Creates separate entities (polylines + text) grouped by name
 * - 'block'/'insert': Creates a block reference with the marker geometry
 */
export type PhotoMarkerMode = "entity" | "insert";

/**
 * What the marker's attribute text displays.
 * - 'number': the internal sequence number (default, unchanged behavior)
 * - 'date': the date/time the marker was placed, formatted per `dateFormat`
 */
export type PhotoMarkerAttribute = "number" | "date";

/**
 * Photo marker config type - uses the shared config from src/blocks/photomarker.ts.
 */
export type PhotoMarkerConfig = (typeof photoMarkers)[number];

/**
 * Mapping from shape type to block name.
 */
const SHAPE_TO_BLOCK_NAME: Record<PhotoMarkerShape, string> = {
  circle: "PHOTO_CIRCLE",
  large_arrow: "PHOTO_LARGE_ARROW",
  thin_arrow: "PHOTO_THIN_ARROW",
  text_only: "PHOTO_TEXT_ONLY",
};

/**
 * Get photo marker config by shape type.
 */
function getConfigByShape(
  shape: PhotoMarkerShape,
): PhotoMarkerConfig | undefined {
  const blockName = SHAPE_TO_BLOCK_NAME[shape];
  return photoMarkers.find((config) => config.name === blockName);
}

/**
 * Undo/redo event detail for photo marker changes.
 */
export interface PhotoMarkerUndoRedoDetail {
  count: number;
}

/**
 * Event name for photo marker undo/redo.
 */
export const PHOTO_MARKER_UNDO_REDO_EVENT = "PhotoMarkerUndoRedo";

/**
 * Manager for photo marker configuration and numbering.
 */
export class PhotoMarkerManager {
  readonly viewer: DRDrawingViewer;

  /** Current marker number (next marker will use this number) */
  private _currentNumber = 1;

  /** Active shape type */
  private _activeShape: PhotoMarkerShape = "circle";

  /** Creation mode: entity (separate geometries) or block (block reference) */
  private _mode: PhotoMarkerMode = "insert";

  /** What the attribute text displays: sequence number or placement date/time */
  private _attributeMode: PhotoMarkerAttribute = "number";

  /** date-fns format string used when attributeMode is 'date' */
  private _dateFormat = "HH:mm:ss";

  /** Change listeners */
  private changeListeners: Array<() => void> = [];

  constructor(viewer: DRDrawingViewer) {
    this.viewer = viewer;
  }

  // ============== Configuration ==============

  /**
   * Get the current marker number.
   */
  get currentNumber(): number {
    return this._currentNumber;
  }

  /**
   * Set the current marker number.
   */
  set currentNumber(value: number) {
    this._currentNumber = Math.max(1, Math.floor(value));
  }

  /**
   * Get the next marker number and increment the counter.
   */
  getNextNumber(): number {
    const num = this._currentNumber;
    this._currentNumber++;
    this.notifyChange();
    return num;
  }

  /**
   * Get the active shape type.
   */
  get activeShape(): PhotoMarkerShape {
    return this._activeShape;
  }

  /**
   * Set the active shape type.
   */
  set activeShape(shape: PhotoMarkerShape) {
    if (getConfigByShape(shape)) {
      this._activeShape = shape;
    }
  }

  /**
   * Get the active photo marker configuration.
   */
  get activeConfig(): PhotoMarkerConfig {
    return getConfigByShape(this._activeShape)!;
  }

  /**
   * Get the creation mode.
   */
  get mode(): PhotoMarkerMode {
    return this._mode;
  }

  /**
   * Set the creation mode.
   */
  set mode(value: PhotoMarkerMode) {
    this._mode = value;
  }

  /**
   * Get the attribute display mode (number or date).
   */
  get attributeMode(): PhotoMarkerAttribute {
    return this._attributeMode;
  }

  /**
   * Set the attribute display mode.
   */
  set attributeMode(value: PhotoMarkerAttribute) {
    this._attributeMode = value;
  }

  /**
   * Get the date-fns format string used when attributeMode is 'date'.
   */
  get dateFormat(): string {
    return this._dateFormat;
  }

  /**
   * Set the date-fns format string used when attributeMode is 'date'.
   */
  set dateFormat(value: string) {
    this._dateFormat = value;
  }

  /**
   * Compute the text to display on the marker's attribute for a given marker number:
   * the number itself, or the current date/time formatted per `dateFormat`.
   */
  private formatLabel(markerNumber: number): string {
    if (this._attributeMode === "date") {
      return formatDate(new Date(), this._dateFormat);
    }
    return String(markerNumber);
  }

  // ============== Counter Initialization ==============

  /**
   * Initialize the marker counter from persisted XRecord data.
   * Should be called after opening a file.
   */
  initializeCounter(): void {
    const db = this.viewer.database;
    const module = this.viewer.module;
    if (!db) return;

    const storedLastNumber = getPhotomarkerLastNumber(module, db, 0);
    if (storedLastNumber > 0) {
      this._currentNumber = storedLastNumber + 1;
    } else {
      this._currentNumber = 1;
    }
    this.notifyChange();
  }

  // ============== Events ==============

  /**
   * Subscribe to changes.
   * @returns Unsubscribe function
   */
  onChange(handler: () => void): () => void {
    this.changeListeners.push(handler);
    return () => {
      const index = this.changeListeners.indexOf(handler);
      if (index >= 0) {
        this.changeListeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify change listeners.
   */
  notifyChange(): void {
    for (const handler of this.changeListeners) {
      handler();
    }
  }

  // ============== Creation ==============

  /**
   * Create a photo marker at the given world position.
   * @returns true if the marker was created successfully
   */
  createAtPosition(
    worldX: number,
    worldY: number,
    layerName: string,
    textStyle?: Textstyle,
    scale?: number,
    isSelected = true,
    entityProps?: EntityProps,
  ): boolean {
    const config = this.activeConfig;
    const markerNumber = this.getNextNumber();
    const label = this.formatLabel(markerNumber);
    const mode = this._mode;
    const result = createPhotoMarker(this.viewer, {
      position: { x: worldX, y: worldY, z: 0 },
      config,
      markerNumber,
      label,
      mode,
      layerName,
      textStyle,
      scale,
    });
    if (!result || result.entityIds.length === 0) return false;

    // Apply noteConfig color to all marker entities (geometry + text)
    if (entityProps?.color && this.viewer.database) {
      for (const id of result.entityIds) {
        applyEntityProps(this.viewer.module, this.viewer.database, id, {
          color: entityProps.color,
        });
      }
    }
    if (isSelected && this.viewer.database) {
      // Select only the block reference (first ID), not the child attributes
      selectionService.selectEntities(
        [result.entityIds[0]],
        this.viewer.module,
        this.viewer.database,
      );
    }
    const command = new AddPhotoMarkerCommand(
      this.viewer,
      {
        entityHandles: result.entityIds.map((id) => getEntityHandle(id)),
        originalLayerName: layerName,
        markerNumber,
      },
      this,
    );

    this.viewer.executeCommand(command);
    trackShape("photo_marker");

    // Persist the current counter to XRecord for fast loading on file reopen
    savePhotomarkerLastNumber(
      this.viewer.module,
      this.viewer.database!,
      markerNumber,
    );

    this.viewer.update();
    for (const id of result.entityIds) {
      deleteObj(id);
    }
    return true;
  }

  // ============== Resize Selected ==============

  /**
   * Returns true if the single selected entity is a block-mode photo marker.
   */
  canResizeSelected(): boolean {
    const ids = selectionService.getAll();
    if (ids.length !== 1) return false;
    const entityId = ids[0];
    return (
      isBlockReference(this.viewer.module, entityId) &&
      isPhotoMarkerEntity(this.viewer.module, entityId)
    );
  }

  /**
   * Returns the attribute text height of the selected photo marker, or null.
   */
  getSelectedTextHeight(): number | null {
    if (!this.canResizeSelected()) return null;
    const ids = selectionService.getAll();
    return getPhotoMarkerTextHeight(this.viewer.module, ids[0]);
  }

  /**
   * Returns the attribute text value of the selected photo marker, or null.
   */
  getSelectedAttributeValue(): string | null {
    if (!this.canResizeSelected()) return null;
    const ids = selectionService.getAll();
    return getPhotoMarkerAttributeValue(this.viewer.module, ids[0]);
  }

  /**
   * Set the attribute text value of the selected photo marker.
   */
  setSelectedAttributeValue(value: string): boolean {
    if (!this.canResizeSelected()) return false;
    const ids = selectionService.getAll();
    const params = setPhotoMarkerAttributeValue(
      this.viewer.module,
      ids[0],
      value,
    );
    if (!params) return false;
    const command = new SetPhotoMarkerAttributeCommand(this.viewer, params);
    this.viewer.executeCommand(command);
    trackShapeChange("photo_marker_value_changed");
    this.viewer.update();
    return true;
  }

  /**
   * Rescale the selected photo marker so its attribute text reaches the given height.
   * The geometry scales proportionally with the text.
   */
  setSelectedTextHeight(height: number): boolean {
    if (!this.canResizeSelected()) return false;
    const ids = selectionService.getAll();
    const params = rescalePhotoMarker(this.viewer.module, ids[0], height);
    if (!params) return false;

    const command = new SetPhotoMarkerScaleCommand(this.viewer, params);
    this.viewer.executeCommand(command);
    this.viewer.update();
    selectionService.updateGrid();
    return true;
  }

  // ============== Helpers ==============

  /**
   * Get all available shape types.
   */
  getAvailableShapes(): PhotoMarkerShape[] {
    return Object.keys(SHAPE_TO_BLOCK_NAME) as PhotoMarkerShape[];
  }

  /**
   * Reset the manager state.
   */
  reset(): void {
    this._currentNumber = 1;
    this._activeShape = "circle";
    this._mode = "entity";
    this._attributeMode = "number";
    this._dateFormat = "HH:mm:ss";
    this.notifyChange();
  }
}
